import type { PermissionKey } from '@nexa/auth';
import type {
  IntegrationDescriptor,
  IntegrationMode,
  IntegrationModeErrorCode,
  IntegrationModeRefusal,
} from './types';

/**
 * THE MODE OF ONE BINDING (providers-by-service M2, D14–D16).
 *
 * `mode` on a row is the BOOT mode: what the declaring app does when the
 * control plane gives no answer. `effectiveMode` is what it uses now, and
 * `selectableModes` are the modes it BUILT and routes to per call. Two or more
 * of those = the operator may choose; anything less = «fijo».
 */

/** The order a choice reads in — the contract's `selectableModesFor` order. */
export const MODE_ORDER: readonly IntegrationMode[] = ['production', 'sandbox', 'mock'];

/** The mode in use now; the boot mode when the BFF sent no `effectiveMode`. */
export function effectiveMode(d: Pick<IntegrationDescriptor, 'mode' | 'effectiveMode'>): IntegrationMode {
  return d.effectiveMode ?? d.mode;
}

/** The modes the operator may pick, in reading order. `[]` = fixed at boot («fijo»). */
export function modeChoices(d: Pick<IntegrationDescriptor, 'selectableModes'>): IntegrationMode[] {
  const offered = new Set(d.selectableModes ?? []);
  const choices = MODE_ORDER.filter((m) => offered.has(m));
  return choices.length >= 2 ? choices : [];
}

/**
 * D16 — the mode moves under the same PERMISSION as the on/off switch. Not the
 * switch's `toggle.allowed`: its reasons (`last_provider`, `credentials_missing`,
 * `not_toggleable`) are about switching on or off, and the mode of the last
 * provider of a service must still be choosable. The BFF enforces it (403).
 */
export const MODE_PERMISSION: PermissionKey = 'providers.toggle';

export function canChangeMode(access: { can(permission: PermissionKey): boolean }): boolean {
  return access.can(MODE_PERMISSION);
}

const REFUSALS: readonly IntegrationModeRefusal[] = [
  'not_found',
  'not_mode_selectable',
  'mode_not_selectable',
  'mode_not_allowed',
  'reason_required',
  'conflict',
];

/** The BFF's answer to a refused write, as one code the dialog can explain. */
export function modeErrorCode(status: number, body: { reason?: unknown; error?: unknown }): IntegrationModeErrorCode {
  const said = body.reason ?? body.error;
  if ((REFUSALS as readonly unknown[]).includes(said)) return said as IntegrationModeRefusal;
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 400) return 'invalid_request';
  return 'unavailable';
}

/** A refused or failed mode write, with its code. */
export class IntegrationModeError extends Error {
  constructor(
    readonly code: IntegrationModeErrorCode,
    readonly status: number,
  ) {
    super(`integration mode refused: ${code} (HTTP ${status})`);
    this.name = 'IntegrationModeError';
  }
}
